"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { comboboxScore } from "@/lib/comboboxSearch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import { Button } from "@/components/Base/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/Base/Command";

export interface ComboboxOption {
  value: string;
  label: string;
  /**
   * Text the search input matches against. Defaults to `label`. Supply this
   * when an option should be findable by terms that aren't displayed (a key
   * tag, an alternate spelling).
   */
  searchText?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  popoverClassName?: string;
  /**
   * Accessible name for the trigger. Optional: `role="combobox"` doesn't take
   * its name from the text it displays, so set this whenever more than one
   * Combobox can be on screen and the placeholder is what tells them apart.
   */
  "aria-label"?: string;
}

export function Combobox({
  options,
  value: controlledValue,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyText = "No option found.",
  disabled = false,
  className,
  popoverClassName,
  "aria-label": ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState("");

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleValueChange = (newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const selectedOption = options.find((option) => option.value === value);

  // cmdk registers each item's `value` prop as its filterable string and
  // never looks at the item's children (see useValue in cmdk's source), so
  // the default filter scores every keystroke against `option.value` — an
  // opaque Firestore doc id for the RO picker. Score the item's keywords
  // instead. `value` has to stay the id because it is exactly what cmdk
  // hands back to onSelect, and callers persist that as a reference.
  //
  // The score is graded (0-3), not a flat 1/0: cmdk auto-selects the
  // top-scoring rendered item and Enter picks it, so a flat score would let
  // list position alone decide which item Enter targets. With RO-descending
  // ordering, an exact-RO-number query like "47" would then select "470"
  // over "47" whenever both are on the board. Grading match quality (exact
  // > prefix > substring) makes quality outrank position; cmdk's sort is
  // stable, so equal-scoring options still keep the order they were
  // supplied in.
  const filterOption = React.useCallback(
    (value: string, search: string, keywords?: string[]) =>
      comboboxScore(keywords?.join(" ") || value, search),
    []
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-[200px] justify-between", className)}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[200px] p-0", popoverClassName)}>
        <Command filter={filterOption}>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.searchText ?? option.label]}
                  onSelect={(currentValue) => {
                    handleValueChange(
                      currentValue === value ? "" : currentValue
                    );
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
